import { motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router";

import { RetentionChartPlaceholder } from "@/components/Review/RetentionChartPlaceholder";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

interface ReviewCompleteProps {
  goBackPath: string;
  reviewedCardsCount: number;
  reviewStartTime: number;
}

export const ReviewComplete = ({
  goBackPath,
  reviewedCardsCount,
  reviewStartTime,
}: ReviewCompleteProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const reviewDurationMinutes = useMemo(() => {
    const duration = Date.now() - reviewStartTime;
    return Math.floor(duration / 1000 / 60) + 1; // Convert milliseconds to minutes
  }, [reviewStartTime]);

  // TODO: somehow calculate the retention increase based on the number of cards reviewed and the review duration.
  const retentionIncreaseDays = Math.floor(Math.random() * 2) + 1;

  // Add keyboard shortcut handler for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Enter" &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        void navigate(goBackPath);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goBackPath, navigate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="flex justify-center"
    >
      <Card className="w-full max-w-lg shadow-lg">
        <CardContent>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Review Complete</h1>

          <p className="text-gray-700 mb-8">
            Nice – you've just reviewed{" "}
            {reviewedCardsCount > 1
              ? `${reviewedCardsCount} cards `
              : `${reviewedCardsCount} card `}
            cards in{" "}
            {reviewDurationMinutes > 1
              ? `${reviewDurationMinutes} minutes `
              : `${reviewDurationMinutes} minute `}
            – and increased your memorization of them by an estimated average of{" "}
            <span className="font-bold">
              {retentionIncreaseDays > 1
                ? `${retentionIncreaseDays} days`
                : `${retentionIncreaseDays} day`}
            </span>
            .
          </p>

          <RetentionChartPlaceholder />

          {/* Complete button */}
          <div className="w-full">
            <Link to={goBackPath}>
              <Button className="w-full py-3 rounded-md relative">
                Complete
                {!isMobile && (
                  <span className="absolute right-2 bg-white/20 text-white text-xs px-2 py-0.5 rounded">
                    ENTER
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
